/**
 * Utility functions for serializing objects to FormData.
 * Used by mutation functions that need to send multipart/form-data requests.
 */

interface MomentLike {
  _isAMomentObject: boolean;
  format: (formatString: string) => string;
}

const isMomentObject = (val: unknown): val is MomentLike => {
  return typeof val === 'object' && val !== null && (val as MomentLike)._isAMomentObject === true;
};

/**
 * Serializes a value to a string suitable for FormData.
 * Handles null/undefined, Moment objects, and primitive values.
 */
export const serializeFormValue = (val: unknown): string => {
  if (val === null || val === undefined) {
    return '';
  }
  if (isMomentObject(val)) {
    return val.format('YYYY-MM-DD');
  }
  return String(val);
};

/**
 * Appends a value to FormData with proper handling for different types.
 * Handles Files, arrays, nested objects, and primitive values.
 */
const appendValueToFormData = (
  formData: FormData,
  key: string,
  value: unknown,
  atlasPublishDetailsKey = 'atlas_publish_details'
): void => {
  if (value === null || value === undefined) {
    return;
  }

  if (value instanceof File) {
    formData.append(key, value);
  } else if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (typeof item === 'object' && item !== null) {
        Object.keys(item).forEach((subKey) => {
          formData.append(`${key}[${index}][${subKey}]`, serializeFormValue((item as Record<string, unknown>)[subKey]));
        });
      } else {
        formData.append(`${key}[${index}]`, serializeFormValue(item));
      }
    });
  } else if (typeof value === 'object' && !isMomentObject(value)) {
    if (key === atlasPublishDetailsKey) {
      Object.keys(value).forEach((fieldKey) => {
        const fieldValue = (value as Record<string, unknown>)[fieldKey];
        if (typeof fieldValue === 'object' && fieldValue !== null) {
          Object.keys(fieldValue).forEach((propKey) => {
            formData.append(
              `${key}[${fieldKey}][${propKey}]`,
              serializeFormValue((fieldValue as Record<string, unknown>)[propKey])
            );
          });
        }
      });
    } else {
      Object.keys(value).forEach((subKey) => {
        formData.append(`${key}[${subKey}]`, serializeFormValue((value as Record<string, unknown>)[subKey]));
      });
    }
  } else {
    formData.append(key, serializeFormValue(value));
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObject = Record<string, any>;

/**
 * Serializes a flat body object to FormData.
 * Use this for simple request bodies without nested 'details' structure.
 */
export const serializeBodyToFormData = <T extends AnyObject>(body: T): FormData => {
  const formData = new FormData();

  Object.keys(body).forEach((key) => {
    appendValueToFormData(formData, key, body[key]);
  });

  return formData;
};

/**
 * Serializes a body object with a 'details' property to FormData.
 * The 'details' object is serialized with 'details[key]' prefixes.
 * Use this for transaction create mutations that have a nested details structure.
 */
export const serializeBodyWithDetailsToFormData = <T extends AnyObject & { details?: AnyObject }>(body: T): FormData => {
  const formData = new FormData();
  const { details, ...restBody } = body;

  Object.keys(restBody).forEach((key) => {
    const value = (restBody as AnyObject)[key];

    if (value === null || value === undefined) {
      return;
    }

    if (value instanceof File) {
      formData.append(key, value);
    } else if (typeof value === 'object' && !isMomentObject(value)) {
      Object.keys(value as object).forEach((subKey) => {
        formData.append(`${key}[${subKey}]`, serializeFormValue((value as Record<string, unknown>)[subKey]));
      });
    } else {
      formData.append(key, serializeFormValue(value));
    }
  });

  if (details) {
    Object.keys(details).forEach((key) => {
      if (key === 'atlas_publish_details') {
        const atlasDetails = details[key] as Record<string, Record<string, unknown>> | null | undefined;
        if (atlasDetails) {
          Object.keys(atlasDetails).forEach((fieldKey) => {
            const fieldValue = atlasDetails[fieldKey];
            if (typeof fieldValue === 'object' && fieldValue !== null) {
              Object.keys(fieldValue).forEach((propKey) => {
                formData.append(
                  `details[${key}][${fieldKey}][${propKey}]`,
                  serializeFormValue((fieldValue as Record<string, unknown>)[propKey])
                );
              });
            }
          });
        }
      } else {
        appendValueToFormData(formData, `details[${key}]`, details[key]);
      }
    });
  }

  return formData;
};
